import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/crm/CrmShell";
import { Btn, Field, inputCls, Modal, money, EmptyState } from "@/components/crm/kit";
import { Pill, Search, UserPlus, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from "lucide-react";

const canon = (text) => {
  const t = (text || "").trim();
  if (!t) return {};
  if (/^\d{5}$/.test(t)) return { origin_zip: t };
  const m = t.match(/^(.+),\s*([A-Za-z]{2})$/);
  if (m) return { origin_city: m[1].trim(), origin_state: m[2].trim() };
  return { origin_address: t };
};
const PAGE = 25;

export default function DrugSearch() {
  const [drug, setDrug] = useState("");
  const [minCost, setMinCost] = useState("");
  const [maxCost, setMaxCost] = useState("");
  const [minClaims, setMinClaims] = useState("");
  const [originInput, setOriginInput] = useState("");
  const [radius, setRadius] = useState("25");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ col: "tot_cost", dir: "desc" });
  const [noData, setNoData] = useState(false);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [handoff, setHandoff] = useState(null);

  useEffect(() => { api.get("/crm/hudlup/meta").then((r) => setUsers(r.data.users || [])).catch(() => {}); }, []);

  const run = useCallback(async (p = 1) => {
    setLoading(true); setSearched(true);
    try {
      const payload = {
        drug: drug.trim() || undefined,
        min_cost: minCost ? Number(minCost) : undefined,
        max_cost: maxCost ? Number(maxCost) : undefined,
        min_claims: minClaims ? Number(minClaims) : undefined,
        sort: sort.col, sort_dir: sort.dir, page: p, page_size: PAGE,
      };
      if (radius && originInput.trim()) { Object.assign(payload, canon(originInput)); payload.radius_mi = Number(radius); }
      const r = await api.post("/medicare/drug-search", payload);
      setRows(r.data.results || []); setTotal(r.data.total || 0); setNoData(!!r.data.no_data); setPage(p);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [drug, minCost, maxCost, minClaims, originInput, radius, sort]);

  useEffect(() => { if (searched) run(1); /* eslint-disable-next-line */ }, [sort]);

  const toggleSort = (col) => setSort((s) => (s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "desc" }));
  const SortTh = ({ col, label, right }) => (
    <th className={`py-2 pr-3 ${right ? "text-right" : ""}`}>
      <button onClick={() => toggleSort(col)} data-testid={`ds-sort-${col}`} className={`inline-flex items-center gap-1 font-bold uppercase tracking-wider ${sort.col === col ? "text-primary" : "text-slate-500"}`}>
        {label}{sort.col === col && (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </button>
    </th>
  );
  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader title="Drug Search" subtitle="Find providers who prescribe a drug — by price and within a radius — so closers can ask the office to write it." icon={Pill} />

      <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
          <div className="col-span-2"><Field label="Drug (brand or generic)"><input data-testid="ds-drug" value={drug} onChange={(e) => setDrug(e.target.value)} className={inputCls} placeholder="e.g. Ozempic or Semaglutide" /></Field></div>
          <Field label="Min drug cost"><input data-testid="ds-min-cost" value={minCost} onChange={(e) => setMinCost(e.target.value)} className={inputCls} placeholder="$" /></Field>
          <Field label="Max drug cost"><input data-testid="ds-max-cost" value={maxCost} onChange={(e) => setMaxCost(e.target.value)} className={inputCls} placeholder="$" /></Field>
          <Field label="Min claims"><input data-testid="ds-min-claims" value={minClaims} onChange={(e) => setMinClaims(e.target.value)} className={inputCls} placeholder="#" /></Field>
          <Field label="Radius (mi)"><input data-testid="ds-radius" value={radius} onChange={(e) => setRadius(e.target.value)} className={inputCls} /></Field>
          <div className="col-span-2"><Field label="Near — address, ZIP or City, ST (optional)"><input data-testid="ds-origin" value={originInput} onChange={(e) => setOriginInput(e.target.value)} className={inputCls} placeholder="06510 or New Haven, CT" /></Field></div>
          <div className="col-span-2 flex items-end"><Btn data-testid="ds-search" onClick={() => run(1)} className="w-full justify-center"><Search className="h-4 w-4" /> Search providers</Btn></div>
        </div>
      </div>

      {noData ? (
        <EmptyState title="No per-drug data loaded yet" sub="An admin can import the CMS Part D 'by Provider and Drug' dataset from Drug Data Import." />
      ) : !searched ? (
        <EmptyState title="Search for a drug" sub="Enter a drug name (and optionally a price range and radius) to find the providers prescribing it." />
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="mb-2 text-sm font-semibold text-slate-600">{total.toLocaleString()} prescriber rows{loading ? " · loading…" : ""}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-slate-500">
                <tr>
                  <SortTh col="name" label="Provider" />
                  <SortTh col="specialty" label="Specialty" />
                  <SortTh col="brand" label="Drug" />
                  <SortTh col="tot_clms" label="Claims" right />
                  <SortTh col="tot_cost" label="Drug Cost" right />
                  <SortTh col="city" label="Location" />
                  <th className="py-2 pr-3 text-right">Dist</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.npi}-${r.brand}-${i}`} className="border-t border-slate-100" data-testid={`ds-row-${i}`}>
                    <td className="py-2 pr-3 font-semibold text-slate-800">{[r.last_name, r.first_name].filter(Boolean).join(", ") || r.npi}</td>
                    <td className="py-2 pr-3 text-slate-500">{r.specialty || "—"}</td>
                    <td className="py-2 pr-3 text-slate-700">{r.brand || "—"}<span className="block text-xs text-slate-400">{r.generic}</span></td>
                    <td className="py-2 pr-3 text-right text-slate-600">{r.tot_clms != null ? Number(r.tot_clms).toLocaleString() : "—"}</td>
                    <td className="py-2 pr-3 text-right font-semibold text-slate-800">{r.tot_cost != null ? money(r.tot_cost) : "—"}</td>
                    <td className="py-2 pr-3 text-slate-500">{[r.city, r.state].filter(Boolean).join(", ")}</td>
                    <td className="py-2 pr-3 text-right text-slate-500">{r.distance_mi != null ? `${Math.round(r.distance_mi)}mi` : "—"}</td>
                    <td className="py-2 text-right"><button data-testid={`ds-handoff-${i}`} onClick={() => setHandoff({ row: r, owner_id: users[0]?.id || "", create_task: true, due_at: "", note: "" })} className="inline-flex items-center gap-1 rounded-lg bg-primary/10 px-2 py-1 text-xs font-bold text-primary hover:bg-primary/20"><UserPlus className="h-3.5 w-3.5" /> Hand off</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className="mt-3 flex items-center justify-end gap-2 text-sm">
              <button disabled={page <= 1} onClick={() => run(page - 1)} className="rounded-lg border border-slate-200 p-1 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
              <span className="text-slate-500">Page {page} / {pages}</span>
              <button disabled={page >= pages} onClick={() => run(page + 1)} className="rounded-lg border border-slate-200 p-1 disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
            </div>
          )}
        </div>
      )}

      <Modal open={!!handoff} onClose={() => setHandoff(null)} title="Hand off to account manager / closer">
        {handoff && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Create a lead for <b>{[handoff.row.last_name, handoff.row.first_name].filter(Boolean).join(", ")}</b> ({handoff.row.brand}) and assign an owner.</p>
            <Field label="Owner"><select data-testid="ds-handoff-owner" value={handoff.owner_id} onChange={(e) => setHandoff({ ...handoff, owner_id: e.target.value })} className={inputCls}><option value="">Select…</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}</select></Field>
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" data-testid="ds-handoff-task" checked={handoff.create_task} onChange={(e) => setHandoff({ ...handoff, create_task: e.target.checked })} /> Create an intro follow-up task</label>
            {handoff.create_task && <Field label="Task due date"><input type="date" data-testid="ds-handoff-due" value={handoff.due_at} onChange={(e) => setHandoff({ ...handoff, due_at: e.target.value })} className={inputCls} /></Field>}
            <Field label="Note (optional)"><input data-testid="ds-handoff-note" value={handoff.note} onChange={(e) => setHandoff({ ...handoff, note: e.target.value })} className={inputCls} placeholder={`Ask the office about writing ${handoff.row.brand}`} /></Field>
            <Btn data-testid="ds-handoff-submit" disabled={!handoff.owner_id} onClick={async () => {
              try {
                const r = await api.post("/crm/referral-influence/handoff", {
                  npi: handoff.row.npi, first_name: handoff.row.first_name, last_name: handoff.row.last_name,
                  specialty: handoff.row.specialty, city: handoff.row.city, state: handoff.row.state,
                  dataset_year: handoff.row.dataset_year, owner_id: handoff.owner_id,
                  create_task: handoff.create_task, due_at: handoff.due_at || undefined,
                  note: handoff.note || `Ask the office about writing ${handoff.row.brand}`,
                });
                toast.success(`Handed off · lead ${r.data.lead_number}${r.data.task_id ? " + task" : ""}`);
                setHandoff(null);
              } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
            }} className="w-full justify-center"><UserPlus className="h-4 w-4" /> Hand off doctor</Btn>
          </div>
        )}
      </Modal>
    </div>
  );
}
